import { useState, useEffect, useRef } from "react";
import { Modal } from "@/components/ui/modal";
import { Table, Input, Popconfirm } from "antd";
import {
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePromptData } from "../../../../../hooks/usePromptData";
import { useQueryClient } from "@tanstack/react-query";
import PromptsModal from "./PromptsModal";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import { TablePagination } from "../../../../../components/pagination/TablePagination";
import { convertToVNTime } from "../../../../../utils/Date";

const PromptsListModal = ({
  // type = "prompts",
  tableProps = {},
  open = false,
  onClose,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null for create, object for edit
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const intl = useIntl();

  // State để lưu trữ dữ liệu hiển thị
  const [localPromptsData, setLocalPromptsData] = useState({
    result: [],
    total: 0,
  });

  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 5,
    name: "",
    text: "",
  });
  const [searchDraft, setSearchDraft] = useState({
    page: 1,
    limit: 5,
    name: "",
    text: "",
  });

  const {
    promptData,
    promptDataLoading,
    deletePromptsByIdAsync,
    importPromptsFileAsync,
    createPromptsAsync,
    updatePromptsAsync,
  } = usePromptData(searchParams);

  // Cập nhật local state khi API data thay đổi
  useEffect(() => {
    if (promptData) {
      setLocalPromptsData(promptData);
    }
  }, [promptData]);

  // Đồng bộ page state với searchParams.page
  useEffect(() => {
    setCurrentPage(searchParams.page);
  }, [searchParams.page]);

  useEffect(() => {
    if (
      !promptDataLoading &&
      localPromptsData?.result?.length === 0 &&
      searchParams.page > 1
    ) {
      setSearchParams((prev) => ({
        ...prev,
        page: 1,
      }));
      const invalidateQueries = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [
            "prompt-data",
            searchParams?.page,
            searchParams?.limit,
            searchParams?.name,
            searchParams?.text,
          ],
        });
      }, 2000);
      return () => clearTimeout(invalidateQueries);
    }
  }, [localPromptsData?.result?.length, promptDataLoading, searchParams.page]);

  const result = localPromptsData?.result || [];

  // Xử lý các sự kiện
  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (item) => {
    try {
      await deletePromptsByIdAsync(item.id);
      setLocalPromptsData((prev) => ({
        ...prev,
        result: prev.result.filter((resultItem) => resultItem.id !== item.id),
        total: (prev.total || 1) - 1,
      }));
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "csv" && fileExtension !== "txt") {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    try {
      const response = await importPromptsFileAsync(file);
      const importedItems =
        response?.imported?.map((row) => {
          const { id, name, text, created_at } = row;
          return {
            id: id,
            name,
            text,
            created_at: created_at,
          };
        }) || [];

      setLocalPromptsData((prev) => {
        const newTotal = (prev.total || 0) + importedItems.length;
        const currentPageSize = prev.result.length;

        // Nếu page hiện tại đã đầy, chuyển về page cuối để hiển thị dữ liệu mới
        if (currentPageSize >= limit) {
          const lastPage = Math.ceil(newTotal / limit);

          setSearchParams((prevParams) => ({
            ...prevParams,
            page: lastPage,
          }));
          const invalidateQueries = setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: [
                "prompt-data",
                searchParams?.page,
                searchParams?.limit,
                searchParams?.name,
                searchParams?.text,
              ],
            });
          }, 2000);
          return () => clearTimeout(invalidateQueries);
        }

        // Nếu page hiện tại còn chỗ, thêm dữ liệu mới vào đầu
        return {
          ...prev,
          result: [...importedItems, ...prev.result],
          total: newTotal,
        };
      });
    } catch (error) {
      console.error("Lỗi khi import:", error);
    } finally {
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSearchParams((prev) => ({
      ...prev,
      ...searchDraft,
      page: newPage,
      limit: limit,
    }));
  };

  const handlePageSizeChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
    setSearchParams((prev) => ({
      ...prev,
      ...searchDraft,
      page: 1,
      limit: newLimit,
    }));
  };

  const handleSearchChange = (e, isNowEmpty = false) => {
    const { name, value } = e.target;
    setSearchDraft((prev) => {
      const nextDraft = { ...prev, [name]: value };
      if (isNowEmpty) {
        setCurrentPage(1);
        setSearchParams((prevParams) => ({
          ...prevParams,
          ...nextDraft,
          page: 1,
        }));
      }
      return nextDraft;
    });
  };

  const handleSearch = () => {
    setSearchParams({
      ...searchParams,
      ...searchDraft,
      page: 1, // Reset về trang đầu tiên khi search
    });
    setCurrentPage(1); // Cập nhật state page để UI đồng bộ
  };

  const handleModalSubmit = async (values) => {
    try {
      if (editingItem) {
        await updatePromptsAsync({
          id: editingItem.id,
          name: values.name,
          text: values.text,
        });
        setLocalPromptsData((prev) => ({
          ...prev,
          result: prev.result.map((it) =>
            it.id === editingItem.id
              ? { ...it, name: values.name, text: values.text }
              : it
          ),
        }));
        setIsModalOpen(false);
        setEditingItem(null);
      } else {
        const response = await createPromptsAsync({
          name: values.name,
          text: values.text,
        });
        const newItem = {
          id: response?.id,
          name: values.name,
          text: values.text,
          created_at: response?.created_at,
        };

        setLocalPromptsData((prev) => {
          const newTotal = (prev.total || 0) + 1;
          const currentPageSize = prev.result.length;

          // Nếu page hiện tại đã đầy, chuyển về page cuối để hiển thị dữ liệu mới
          if (currentPageSize >= limit) {
            const lastPage = Math.ceil(newTotal / limit);

            setSearchParams((prevParams) => ({
              ...prevParams,
              page: lastPage,
            }));
            const invalidateQueries = setTimeout(() => {
              queryClient.invalidateQueries({
                queryKey: [
                  "prompt-data",
                  searchParams?.page,
                  searchParams?.limit,
                  searchParams?.name,
                  searchParams?.text,
                ],
              });
            }, 2000);
            return () => clearTimeout(invalidateQueries);
          }

          // Nếu page hiện tại còn chỗ, thêm dữ liệu mới vào đầu
          return {
            ...prev,
            result: [newItem, ...prev.result],
            total: newTotal,
          };
        });
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  // Cấu hình cột
  const columns = [
    {
      key: "name",
      dataIndex: "name",
      title: <FormattedMessage id="AGENTS.HOME.TABLE_COLUMN_NAME" />,
      width: "200px",
      align: "left",
    },
    {
      key: "text",
      dataIndex: "text",
      title: <FormattedMessage id="AGENTS.HOME.TABLE_COLUMN_TEXT" />,
      type: "text",
      width: "500px",
      align: "left",
    },
    {
      key: "created_at",
      dataIndex: "created_at",
      title: <FormattedMessage id="AGENTS.HOME.TABLE_COLUMN_CREATED_DATE" />,
      type: "text",
      width: "150px",
      align: "center",
      render: (text) => <p>{convertToVNTime(text)}</p>,
    },
    {
      key: "action",
      dataIndex: "action",
      title: <FormattedMessage id="AGENTS.REFERENCE_DATA.ACTION" />,
      type: "actions",
      width: "100px",
      align: "center",
      render: (_value, record) => (
        <div className="flex gap-4 justify-center ">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
            className="text-blue-500 hover:text-blue-300"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
          <Popconfirm
            title={<FormattedMessage id="AGENTS.PROMPTS.MODAL.DELETE_TASK" />}
            description={
              <FormattedMessage id="AGENTS.PROMPTS.MODAL.DELETE_CONFIRM" />
            }
            onConfirm={() => handleDelete(record)}
            okText={<FormattedMessage id="AGENTS.REFERENCE_DATA.YES" />}
            cancelText={<FormattedMessage id="AGENTS.REFERENCE_DATA.NO" />}
          >
            <button className="text-red-500 hover:text-red-300">
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${intl.formatMessage({ id: "AGENTS.PROMPTS.MODAL.MANAGE_TITLE" })}`}
      width="90vw"
      maxWidth="100vw"
    >
      <div className="">
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".csv,.txt"
          onChange={handleFileChange}
        />
        <div className="flex flex-col justify-between md:flex-row md:items-center mb-4 gap-4">
          <div className="flex gap-3">
            <Input
              placeholder={intl.formatMessage({
                id: "AGENTS.TABLE_TOOLBAR.SEARCH",
              })}
              prefix={
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="text-gray-400"
                />
              }
              allowClear
              className="w-full md:w-80"
              name="name"
              value={searchDraft.name}
              onChange={handleSearchChange}
              onPressEnter={handleSearch}
              onClear={() =>
                handleSearchChange(
                  { target: { name: "name", value: "" } },
                  true
                )
              }
            />
            <button
              className="px-4 py-1.5 bg-green-500 hover:bg-green-400 text-white rounded-lg flex items-center gap-2 whitespace-nowrap"
              onClick={handleSearch}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <FormattedMessage id="AGENTS.TABLE_TOOLBAR.SEARCH" />
            </button>

            <div className="flex flex-wrap items-center gap-2">
              <button
                className="px-4 py-1.5 bg-[#7239EA] text-white rounded-lg flex items-center gap-2 whitespace-nowrap"
                onClick={handleCreate}
              >
                <FontAwesomeIcon icon={faPlus} />{" "}
                <FormattedMessage id="AGENTS.HOME.CREATE" />
              </button>
              {/* <button
                className="px-4 py-1.5 bg-green-500 text-white rounded-lg flex items-center gap-2 whitespace-nowrap"
                onClick={handleImportCSV}
              >
                <FontAwesomeIcon icon={faFileCsv} />{" "}
                <FormattedMessage id="AGENTS.HOME.IMPORT_CSV" />
              </button> */}
              {/* <Tooltip
                title={
                  <span>
                    Tải xuống mẫu file{" "}
                    <a
                      href={"https://media.chatme.vn/export_temp/temp_prompts.csv"}
                      rel="noopener noreferrer"
                      className="text-blue-500 underline-line"
                    >
                      Tại đây
                    </a>
                  </span>
                }
                placement="topLeft"
              >
                <KeenIcon
                  icon="question"
                  className="text-2xl text-gray-500 cursor-help"
                />
              </Tooltip> */}
            </div>
          </div>
          <div className="flex">
            <TablePagination
              current={currentPage}
              pageSize={limit}
              total={localPromptsData?.total || 0}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
              pageSizeOptions={["5", "10", "20", "50", "80", "100"]}
            />
          </div>
        </div>
        <Table
          dataSource={result}
          loading={promptDataLoading}
          columns={columns}
          rowKey="id"
          pagination={false}
          scroll={{ x: "max-content" }}
          bordered
          size="large"
          locale={{
            emptyText: <FormattedMessage id="AGENTS.REFERENCE_DATA.NO_DATA" />,
          }}
          className="custom-table"
          onRow={() => ({
            className: "hover:bg-gray-50",
          })}
          {...tableProps}
        />

        <PromptsModal
          isEditing={!!editingItem}
          open={isModalOpen}
          onCancel={handleModalCancel}
          onFinish={handleModalSubmit}
          initialValues={editingItem}
        />
      </div>
      <div className="flex justify-end mt-3 flex-wrap gap-3 mb-4 items-center">
        <div className="flex">
          <TablePagination
            current={currentPage}
            pageSize={limit}
            total={localPromptsData?.total || 0}
            onChange={handlePageChange}
            onShowSizeChange={handlePageSizeChange}
            pageSizeOptions={["5", "10", "20", "50", "80", "100"]}
          />
        </div>
      </div>
    </Modal>
  );
};

PromptsListModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // type: PropTypes.string.isRequired,
  tableProps: PropTypes.object,
};

export { PromptsListModal };
